import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Download, RotateCcw } from "lucide-react";
import { ServicesURL, downloadBlobFile } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { DynRuleSet } from "./types";
import InfoBannerCst from "@components/InfoBannerCst";

interface PreviewCountBannerProps {
  corporateId: string;
  sets: DynRuleSet[];
}

const buildPreviewSets = (sets: DynRuleSet[]) =>
  sets
    .map((set) => ({
      operator: set.operator || "AND",
      rules: (set.rules || [])
        .filter((r) => r.condition_field && r.condition)
        .map((r, index) => ({
          operator: r.operator || "AND",
          condition_field: r.condition_field,
          condition: r.condition,
          value: r.value,
          position: index + 1,
        })),
    }))
    .filter((set) => set.rules.length > 0);

const PreviewCountBanner: React.FC<PreviewCountBannerProps> = ({
  corporateId,
  sets,
}) => {
  const { t } = useTranslation("group-wizard-v2");
  const theme = useTheme();

  const [dismissed, setDismissed] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [isCounting, setIsCounting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const previewSets = useMemo(() => buildPreviewSets(sets), [sets]);
  const hasRules = previewSets.length > 0;

  if (dismissed) return null;

  const handleRecalc = async () => {
    setIsCounting(true);
    try {
      const res: any = await fetcher(
        ServicesURL.dynamicGroupPreviewCount.replace(
          ":idCorporate",
          corporateId,
        ),
        MethodType.POST,
        { sets: previewSets },
      );
      setCount(res?.count ?? res?.data?.count ?? 0);
    } catch (e: any) {
      openToast({
        severity: "error",
        message: e?.message || String(t("rules.preview.error")),
      });
    } finally {
      setIsCounting(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadBlobFile(
        ServicesURL.dynamicGroupPreviewExport.replace(
          ":idCorporate",
          corporateId,
        ),
        { sets: previewSets },
        "dynamic-group-users.csv",
      );
    } catch (e: any) {
      openToast({
        severity: "error",
        message: e?.message || String(t("rules.preview.error")),
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <InfoBannerCst onClose={() => setDismissed(true)}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Typography
          sx={{ fontSize: "15px", fontWeight: 700, color: "inherit" }}
        >
          {t("rules.preview.title")}
        </Typography>

        {count === null && (
          <Typography
            sx={{ fontSize: "13px", fontWeight: 700, color: "inherit" }}
          >
            {t("rules.preview.hint")}
          </Typography>
        )}

        <Typography sx={{ fontSize: "13px", color: "inherit" }}>
          <Box component="strong" sx={{ fontWeight: 700 }}>
            {t("rules.preview.countLabel", { count: count ?? 0 })}
          </Box>{" "}
          {t("rules.preview.countSuffix")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mt: 0.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            endIcon={
              isCounting ? (
                <CircularProgress size={14} />
              ) : (
                <RotateCcw size={16} />
              )
            }
            onClick={handleRecalc}
            disabled={isCounting || !hasRules}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              borderColor: "#000000",
              color: "#000000",
              "&:hover": {
                borderColor: "#000000",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            {t("rules.preview.recalc")}
          </Button>
          <Button
            variant="contained"
            endIcon={<Download size={16} />}
            onClick={handleExport}
            disabled={isExporting || !hasRules}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 700,
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.background.default,
                boxShadow: "none",
                opacity: 0.85,
              },
            }}
          >
            {t("rules.preview.exportCsv")}
          </Button>
        </Box>
      </Box>
    </InfoBannerCst>
  );
};

export default PreviewCountBanner;
